(() => {
  class SearchResultComponent extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        imageContainerElem: this.querySelector(".search-result-item__image-container"),
        linkElem: this.querySelector(".search-result-item__link"),
        imageElem: this.querySelector(".search-result-item__image-container img"),
        titleElem: this.querySelector(".search-result-item__body-title"),
        currentPriceElem: this.querySelector(".search-result-item__price-current"),
        compareAtPriceElem: this.querySelector(".search-result-item__price-compare-at"),
      };

      this.elements.imageElem.onload = () => {
        this.elements.imageContainerElem.classList.remove("search-result-item__image-container--pending");
      };
     
      this._currentView = undefined
    }

    setLoading() {
      this.setTitle("");
      this.setLink("");
      this.removePrice();

      this.elements.imageElem.removeAttribute("src");

      this.classList.add("search-result-item__container--loading");
      this.classList.add("search-results__result--loading");
    }

    setLoaded() {
      this.classList.remove("search-result-item__container--loading");
      this.classList.remove("search-results__result--loading");
    }

    setLink(link) {
      this.elements.linkElem.href = link;
    }

    setTitle(title) {
      this.elements.titleElem.textContent = title;
    }

    setPrice(basePrice) {
      this.elements.currentPriceElem.textContent = basePrice;

      this.elements.compareAtPriceElem.textContent = "";
      this.elements.compareAtPriceElem.classList.remove("search-result-item__price-compare-at--discounted");
    }

    setDiscountedPrice(currentPrice, compareAtPrice) {
      this.elements.currentPriceElem.textContent = currentPrice;

      this.elements.compareAtPriceElem.textContent = compareAtPrice;
      this.elements.compareAtPriceElem.classList.add("search-result-item__price-compare-at--discounted");
    }

    removePrice() {
      this.setDiscountedPrice("", "");
    }

    setImageContent(data) {
      if (data.url) {
        const currentSrc = this.elements.imageElem.getAttribute("src");

        if (currentSrc !== data.url) {
          this.elements.imageContainerElem.classList.add("search-result-item__image-container--pending");
          this.elements.imageElem.setAttribute("src", data.url);
        } else {
          this.elements.imageContainerElem.classList.remove("search-result-item__image-container--pending");
        }
      }

      if (data.alt) this.elements.imageElem.setAttribute("alt", data.alt);

      if (data.aspectRatio) {
        this.elements.imageElem.setAttribute("aspect-ratio", data.aspectRatio);
      }
    }
  }

  if (!customElements.get("search-result")) {
    customElements.define("search-result", SearchResultComponent);
  }

  class SearchResultsManager {
    constructor(searchContainer, options = {}) {
      if (!(searchContainer instanceof HTMLElement)) {
        throw new Error("searchContainer must be an HTMLElement");
      }

      this.searchContainer = searchContainer;

      this.options = options;

      if (Array.isArray(window.dataBestSellersRaw)) {
        this.bestSellers = window.dataBestSellersRaw.map(this._mapBestSeller);
      } else {
        this.bestSellers = [];
      }

      this.defaultSuggestedSearches = ["Black Suits", "Blazer", "Double Breasted Suits", "Gift Cards", "Tuxedo"];

      this.searchResultAmount = options.searchResultAmount

      this.elements = {
        searchViewEmpty: searchContainer.querySelector(".search-view__container"),
        searchViewResults: searchContainer.querySelector(".search-results__container"),
        searchError: searchContainer.querySelector(".search-view__error"),
        currentSearchText: searchContainer.querySelector(".search-view__your-search"),
        searchResultsList: searchContainer.querySelector(".search-results__list"),
        popularSearchesText: searchContainer.querySelector(".new-popular-searches__title--search-subtitle"),
        suggestedSearches: searchContainer.querySelector(".new-popular-searches__list"),
        seeAllResultsContainer: searchContainer.querySelector(".search-results__see-more-container"),
        seeAllResultsLink: searchContainer.querySelector(".search-results__see-more-link")
      };

      Object.entries(this.elements).forEach(([key, element]) => {
        if (!element) {
          throw new Error(`Required element not found: ${key}`);
        }
      });

      this.abortController = new AbortController();

      this.searchInputTimeout = undefined;

      this.currentTransition = undefined;

      this.mounted = false;

      this._popularSearchCleanups = [];
    }

    _mapBestSeller = (product) => {
      return {
        compareAtPriceMin: Shopify.formatMoney(product.compare_at_price_min),
        compareAtPriceMinRaw: product.compare_at_price_min,
        priceMin: Shopify.formatMoney(product.price_min),
        priceMinRaw: product.price_min,
        title: product.title,
        url: `/products/${product.handle}`,
        featuredImage: {
          alt: product.media?.[0]?.alt || null,
          aspectRatio: product.media?.[0]?.aspect_ratio || 0,
          url: product.media?.[0]?.src || product.featured_image,
        },
      };
    };

    mount() {
      if (this.mounted) return;

      this._setupPopularSearchAccessibility();
      this.mounted = true;

      if (this.options.onMount !== undefined) this.options.onMount();
    }

    _setupPopularSearchAccessibility() {
      const items = this.elements.suggestedSearches.querySelectorAll(".new-popular-searches__item");

      items.forEach((item) => {
        item.setAttribute("tabindex", "0");
        item.setAttribute("role", "button");

        const handleActivate = () => {
          const searchTerm = item.getAttribute("data-search-term");
          if (!searchTerm) return;

          if (this.options.onPopularSearchSelect) {
            this.options.onPopularSearchSelect(searchTerm);
          }
        };

        const handleKeydown = (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleActivate();
          }
        };

        item.addEventListener("click", handleActivate);
        item.addEventListener("keydown", handleKeydown);

        this._popularSearchCleanups.push(
          () => item.removeEventListener("click", handleActivate),
          () => item.removeEventListener("keydown", handleKeydown),
        );
      });
    }

    unmount() {
      if (!this.mounted) return;

      this._cancelSearch();

      if (this.currentTransition) {
        this.currentTransition.skipTransition();
        this.currentTransition = undefined;
      }

      this._popularSearchCleanups.forEach((cleanup) => cleanup());
      this._popularSearchCleanups = [];

      this.mounted = false;

      if (this.options.onUnmount !== undefined) this.options.onUnmount();
    }

    _cancelSearch() {
      clearTimeout(this.searchInputTimeout);
      this.searchInputTimeout = undefined;
      this.abortController.abort();
    }

    _hideShowMoreButton() {
      this.elements.seeAllResultsContainer.classList.add("search-results__see-more-container--hidden");
      this.elements.seeAllResultsLink.setAttribute("aria-hidden", "true");
      this.elements.seeAllResultsLink.href = "";
    }

    _showShowMoreButton(href) {
      this.elements.seeAllResultsContainer.classList.remove("search-results__see-more-container--hidden");
      this.elements.seeAllResultsLink.setAttribute("aria-hidden", "false");
      this.elements.seeAllResultsLink.href = href;
    }

    search(query, options = {}) {
      const { debounceMs = 500 } = options;

      if (!this.mounted) {
        console.warn("SearchResultsManager: Cannot search while unmounted");
        return;
      }

      this._cancelSearch();

      const trimmedQuery = query.trim();

      const handleError = (error) => {
        if (error.name === "AbortError") return;
        this._showError(`Unexpected error while searching: ${error.message}. Please try again in a few moments.`);
        if (this.options.onSearchError !== undefined) this.options.onSearchError();
      };

      const handleSuccess = (searchResults) => {
        this._hideError();

        const helperText = trimmedQuery === "" ? "You did not search for anything" : `Your search for ${trimmedQuery}`;
        this._showShowMoreButton(`/search?type=product&q=${encodeURIComponent(query)}`);

        if (searchResults.products.length > 0) {
          this._switchSearchView("results");
          this._populateSearchResults(searchResults.products);
          this._updateSuggestedSearches(searchResults.suggestedSearches);

          this.elements.popularSearchesText.textContent = `Your search for '${trimmedQuery}'`;
          this.elements.popularSearchesText.setAttribute("aria-hidden", true);
        } else {
          this._switchSearchView("empty");
          this.elements.currentSearchText.textContent = helperText;
        }

        if (this.options.onSearchFinish) this.options.onSearchFinish();
      };

      const executeSearch = () => {
        if (trimmedQuery.length === 0) {
          this.showBestSellers();
          if (this.onSearchFinish !== undefined) this.onSearchFinish();
        } else {
          this._setLoading();
          if (this.options.onSearchLoading) this.options.onSearchLoading();
          this.searchInputTimeout = undefined;
          this._performSearch(trimmedQuery, handleSuccess, handleError);
        }
      };

      this.searchInputTimeout = setTimeout(executeSearch, debounceMs);
    }

    showBestSellers = (animateTransition = true) => {
      this._cancelSearch();
      this.elements.popularSearchesText.innerHTML = "Best Sellers";
      this.elements.popularSearchesText.setAttribute("aria-hidden", false);

      this._switchSearchView("results", animateTransition);
      this._populateSearchResults(this.bestSellers);
      this._updateSuggestedSearches(this.defaultSuggestedSearches);
      this._hideShowMoreButton();
    };

    reset() {
      this._hideError();
      this._switchSearchView("empty", false);
      this.elements.currentSearchText.textContent = "Search The Store";
    }

    _generateSearchUri(encodedQuery) {
      return `/search/suggest.json?q=${encodedQuery}&resources[type]=product,query&resources[limit]=${this.searchResultAmount}&resources[limit_scope]=each`;
    }

    _showError(message) {
      this._switchSearchView("empty");
      this.elements.searchError.textContent = message;
      this.elements.searchError.setAttribute("aria-hidden", "false");
      this.elements.searchError.hidden = false;
    }

    _hideError() {
      this.elements.searchError.textContent = "";
      this.elements.searchError.setAttribute("aria-hidden", "true");
      this.elements.searchError.hidden = true;
    }

    _mapSingleSearchResult(productData) {
      return {
        isAvailable: productData.available,
        body: productData.body,
        compareAtPrice: Shopify.formatMoney(productData.compare_at_price),
        compareAtPriceMax: Shopify.formatMoney(productData.compare_at_price_max),
        compareAtPriceMin: Shopify.formatMoney(productData.compare_at_price_min),
        compareAtPriceMinRaw: productData.compare_at_price_min,
        handle: productData.handle,
        id: productData.id,
        rawImageUrl: productData.image,
        price: Shopify.formatMoney(productData.price),
        priceMin: Shopify.formatMoney(productData.price_min),
        priceMinRaw: productData.price_min,
        priceMax: Shopify.formatMoney(productData.price_max),
        tags: productData.tags,
        title: productData.title,
        url: productData.url,
        featuredImage: {
          alt: productData.featured_image.alt,
          aspectRatio: productData.featured_image.aspect_ratio,
          heightPixels: productData.featured_image.height,
          widthPixels: productData.featured_image.width,
          url: productData.featured_image.url,
        },
      };
    }

    _mapSearchResults(jsonResponse) {
      const parseProducts = () => {
        const products = jsonResponse?.resources?.results?.products;
        if (!products || !Array.isArray(products)) return [];

        return products.map(this._mapSingleSearchResult);
      };

      const capitaliseSentence = (s) => {
        let result = "";
        let lastCharacterSpace = true;
        for (let i = 0; i < s.length; i++) {
          const current = s[i];

          if (lastCharacterSpace) result += current.toUpperCase();
          else result += current;

          lastCharacterSpace = current == " ";
        }
        return result;
      };

      const parseQueries = () => {
        const suggestedSearches = jsonResponse?.resources?.results?.queries;
        if (!suggestedSearches || !Array.isArray(suggestedSearches)) return [];

        const mapped = suggestedSearches.map((s) => capitaliseSentence(s.text));
        return mapped;
      };

      return {
        products: parseProducts(),
        suggestedSearches: parseQueries(),
      };
    }

    _performSearch(rawQuery, onComplete, onFail) {
      const input = encodeURIComponent(rawQuery.trim());
      const uri = this._generateSearchUri(input);
      this.abortController = new AbortController();

      fetch(uri, { signal: this.abortController.signal })
        .then((response) => {
          const contentType = response.headers.get("content-type");
          if (!contentType) throw new Error("Content-Type header not provided");
          if (!contentType.toLowerCase().includes("application/json")) {
            throw new Error(`Received unexpected Content-Type from response: ${contentType}`);
          }
          return response;
        })
        .then((response) => {
          if (!response.ok) throw new Error("Search request failed");
          return response;
        })
        .then((response) => response.json())
        .then((json) => this._mapSearchResults(json))
        .then(onComplete)
        .catch(onFail);
    }

    _updateSearchResultState(searchResult, element) {
      element.setTitle(searchResult.title);
      element.setLink(searchResult.url);
      element.setImageContent(searchResult.featuredImage);

      if (searchResult.compareAtPriceMinRaw > searchResult.priceMinRaw) {
        element.setDiscountedPrice(searchResult.priceMin, searchResult.compareAtPriceMin);
      } else {
        element.setPrice(searchResult.priceMin);
      }

      element.setLoaded();
    }

    _updateSearchResultList(results) {
      const listElements = [...this.elements.searchResultsList.querySelectorAll(".search-results__result")];
      const elementsToEnable = Math.min(results.length, listElements.length);

      for (let i = 0; i < elementsToEnable; i++) {
        const currentResult = results[i];
        const currentElement = listElements[i];

        currentElement.classList.remove("search-results__result--hidden");

        this._updateSearchResultState(currentResult, currentElement);
      }

      for (let i = elementsToEnable; i < listElements.length; i++) {
        const currentElement = listElements[i];

        currentElement.classList.add("search-results__result--hidden");
      }
    }

    _setLoading() {
      const listElements = [...this.elements.searchResultsList.querySelectorAll(".search-results__result:not(.search-results__result--hidden)")];

      for (let i = 0; i < listElements.length; i++) {
        listElements[i].setLoading();
      }

      const suggestedItems = this.elements.suggestedSearches.querySelectorAll(".new-popular-searches__item");
      for (let i = 0; i < suggestedItems.length; i++) {
        suggestedItems[i].classList.add("new-popular-searches__item--skeleton");
      }
    }

    _updateSuggestedSearches(searches) {
      const items = [...this.elements.suggestedSearches.querySelectorAll(".new-popular-searches__item")];
      const searchesToShow = searches.slice(0, 5);

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (i < searchesToShow.length) {
          const searchTerm = searchesToShow[i];

          item.classList.remove("new-popular-searches__item--skeleton");
          item.classList.remove("new-popular-searches__item--hidden");
          item.setAttribute("data-search-term", searchTerm);
          item.setAttribute("aria-label", `Search for ${searchTerm}`);
          item.setAttribute("tabindex", "0");

          const textElem = item.querySelector(".new-popular-searches__item-text");
          if (textElem) {
            textElem.textContent = searchTerm;
          }
        } else {
          item.classList.remove("new-popular-searches__item--skeleton");
          item.classList.add("new-popular-searches__item--hidden");
          item.setAttribute("tabindex", "-1");
        }
      }
    }

    _populateSearchResults(results) {
      this._updateSearchResultList(results);
    }

    _switchSearchView(view, animate = true) {
      const switchEmpty = () => {
        this.elements.searchViewEmpty.classList.add("search-view__container--active");
        this.elements.searchViewResults.classList.remove("search-results__container--active");
      };

      const switchResults = () => {
        this.elements.searchViewEmpty.classList.remove("search-view__container--active");
        this.elements.searchViewResults.classList.add("search-results__container--active");
      };

      const updateDOM = () => {
        if (this._currentView !== "empty" && view === "empty") {
          const prev = this._currentView
          this._currentView = "empty"
          switchEmpty();
          if (this.options.onSwitchView) this.options.onSwitchView(prev, "empty")
        } else if (this._currentView !== "results" && view === "results") {
          const prev = this._currentView
          this._currentView = "results"
          switchResults();
          if (this.options.onSwitchView) this.options.onSwitchView(prev, "results")
        }
      };

      if (animate && document.startViewTransition) {
        if (this.currentTransition) {
          this.currentTransition.skipTransition();
        }

        this.currentTransition = document.startViewTransition(updateDOM);
      } else {
        updateDOM();
      }
    }
  }

  window.SearchResultsManager = SearchResultsManager;
  window.SearchResultComponent = SearchResultComponent;
})();
